import { useCallback, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useGetSyncRecordsQuery, useResyncRecordsMutation } from '../../../../../features/syncReports/syncReportsApi'
const EMPTY_ID_FILTERS = { messageId: '', sourceId: '', targetId: '', reference: '' }
export const EMPTY_FILTERS = { ...EMPTY_ID_FILTERS, range: null }
const EMPTY = []
const useSummaryDetails = (request, range, onRangeChange) => {
    const [idFilters, setIdFilters] = useState(EMPTY_ID_FILTERS)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSizeState] = useState(10)
    const [selected, setSelected] = useState([])
    const [status, setStatus] = useState(null)

    const recordsQuery = useGetSyncRecordsQuery(
        request
            ? {
                ...request,
                range,
                messageId: idFilters.messageId.trim(),
                sourceId: idFilters.sourceId.trim(),
                targetId: idFilters.targetId.trim(),
                reference: idFilters.reference.trim(),
                page,
                pageSize
            }
            : skipToken,
        { refetchOnMountOrArgChange: true }
    )
    const [resyncRecords, { isLoading: isResyncing }] = useResyncRecordsMutation()

    const startOver = () => {
        setPage(1)
        setSelected([])
    }
    const setFilters = (next) => {
        setIdFilters({
            messageId: next.messageId || '',
            sourceId: next.sourceId || '',
            targetId: next.targetId || '',
            reference: next.reference || ''
        })
        if (next.range !== range) {
            onRangeChange(next.range)
        }
        startOver()
    }

    const setPageSize = (size) => {
        setPageSizeState(size)
        startOver()
    }

    const goToPage = (next) => {
        setPage(next)
        setSelected([])
    }

    const toggleSelected = (messageId) =>
        setSelected((current) =>
            current.indexOf(messageId) === -1 ? current.concat(messageId) : current.filter((id) => id !== messageId)
        )

    const resync = useCallback(() => {
        if (!request || selected.length === 0 || isResyncing) return
        const count = selected.length
        setStatus(null)

        resyncRecords({ requestType: request.requestType, messageIds: selected })
            .unwrap()
            .then((response) => {
                if (response && response.result) {
                    setSelected([])
                    setStatus({
                        tone: 'success',
                        message: `${count} ${count === 1 ? 'record' : 'records'} queued for re-sync. The list refreshes in a few seconds.`
                    })
                } else {
                    setStatus({ tone: 'error', message: 'The selected records are still being processed. Please try again shortly.' })
                }
            })
            .catch(() => setStatus({ tone: 'error', message: 'Re-sync failed. Please try again.' }))
    }, [request, selected, isResyncing, resyncRecords])

    const data = recordsQuery.data

    return {
        filters: { ...idFilters, range },
        setFilters,
        page,
        goToPage,
        pageSize,
        setPageSize,
        records: data ? data.records : EMPTY,
        totalRecords: data ? data.totalRecords : 0,
        isLoading: recordsQuery.isFetching,
        loadError: recordsQuery.isError ? 'Unable to load the records. Please try again.' : '',
        reload: () => {
            if (request) recordsQuery.refetch()
        },
        selected,
        setSelected,
        toggleSelected,
        resync,
        isResyncing,
        status,
        dismissStatus: () => setStatus(null)
    }
}

export default useSummaryDetails
